import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '@/services/api';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * HeatwaveSimulator — simulateur de pré-positionnement (panneau droit du Plan Canicule).
 * L'utilisateur coche des actions concrètes → POST /simulations → gain de couverture.
 *
 * ⚠ Valeurs de démonstration. En prod, le back recalcule sur isochrones réels.
 */

export interface HeatAction {
  id: string;
  label: string;
  desc: string;
  cover: number;
  deserts: number;
  refuges: number;
}

export interface SimResult {
  coverage_after: number;
  deserts_closed: number;
  refuges_added: number;
  pop_gain: number;
  by_action: { id: string; label: string; cover: number }[];
}

export const HEAT_ACTIONS: HeatAction[] = [
  { id: 'gym',   label: 'Ouvrir le gymnase climatisé',   desc: 'Complexe M.Berteaux · +1 refuge',      cover: 14, deserts: 2, refuges: 1 },
  { id: 'brume', label: 'Installer des brumisateurs',    desc: '3 placettes du centre',                cover: 5,  deserts: 1, refuges: 3 },
  { id: 'media', label: 'Étendre les horaires médiathèque', desc: "Nocturne fraîche jusqu'à 22 h",    cover: 6,  deserts: 1, refuges: 0 },
  { id: 'ccas',  label: 'Renforcer les visites du CCAS', desc: 'Registre canicule · 412 inscrits',     cover: 9,  deserts: 0, refuges: 0 },
];

interface Props {
  /** Couverture initiale avant simulation (%). Vient de HeatwaveData.coverage_pct. */
  coverBaseline: number;
  /** Population vulnérable totale. Vient de HeatwaveData.pop_vulnerable. */
  popVulnerable: number;
  onResult?: (r: SimResult | null) => void;
}

export function HeatwaveSimulator({ coverBaseline, popVulnerable, onResult }: Props) {
  const { activeTerritory } = useTerritoryStore();
  const [selected, setSelected] = useState<string[]>([]);

  const sim = useMutation<SimResult, Error, string[]>({
    mutationFn: async (actions: string[]) => {
      const { data } = await api.post<SimResult>('/simulations', {
        territory_code: activeTerritory?.code_insee,
        name: `sim-heatwave_response-${Date.now()}`,
        type: 'heatwave_response',
        parameters: { actions },
      });
      return data;
    },
    onSuccess: (r) => onResult?.(r),
  });

  const toggle = (id: string) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    sim.reset();
    onResult?.(null);
  };

  const r = sim.data;
  const canSimulate = selected.length > 0 && !sim.isPending;

  return (
    <div className="flex flex-col gap-3 rounded-panel border border-line bg-white p-[18px] shadow-card h-full overflow-y-auto">
      <div>
        <h3 className="font-display text-[15px] font-bold text-ink">Simulateur de pré-positionnement</h3>
        <p className="mt-0.5 text-xs text-faint">Anticiper la prochaine vague</p>
      </div>

      {/* Liste des actions */}
      <div className="flex flex-col gap-2">
        {HEAT_ACTIONS.map((a) => {
          const on = selected.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              className={`flex items-center gap-3 rounded-[11px] border px-3 py-2.5 text-left transition ${
                on ? 'border-[#9bb8ee] bg-[#eef4ff]' : 'border-line bg-white hover:border-muted'
              }`}
            >
              {/* Checkbox accessible */}
              <span
                aria-hidden="true"
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
                  on ? 'border-navy bg-navy' : 'border-[#cbd3e2] bg-white'
                }`}
              >
                {on && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </span>
              <span className="flex-1">
                <span className="block text-[13.5px] font-semibold text-ink">{a.label}</span>
                <span className="block text-[11.5px] text-faint">{a.desc}</span>
              </span>
              <span className="flex-shrink-0 font-mono text-xs font-semibold text-[#1f8a5b]">
                +{a.cover} pts
              </span>
            </button>
          );
        })}
      </div>

      {/* Bouton simuler */}
      <button
        type="button"
        disabled={!canSimulate}
        onClick={() => sim.mutate(selected)}
        className="mt-1 flex h-[46px] w-full items-center justify-center gap-2 rounded-[11px] text-sm font-bold text-white transition disabled:cursor-not-allowed"
        style={{ background: canSimulate ? '#e1000f' : '#cbd3e2' }}
      >
        {sim.isPending ? 'Simulation…' : 'Simuler le pré-positionnement'}
      </button>

      {sim.isError && (
        <p className="text-xs text-[#e1000f]" role="alert">
          Erreur de simulation. Veuillez réessayer.
        </p>
      )}

      {/* Résultat de la simulation */}
      {r && (
        <div className="rounded-[13px] border border-[#cfe7d8] bg-gradient-to-b from-[#eef8f1] to-[#e3f3ea] p-[15px]">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1f8a5b]">
                Couverture &lt; 10 min
              </div>
              <div className="font-display text-[30px] font-extrabold leading-none text-[#1f8a5b]">
                {r.coverage_after} %
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-[#5b6480]">Déserts refermés</div>
              <div className="font-display text-lg font-bold text-ink">{r.deserts_closed}</div>
            </div>
          </div>
          <p className="mt-2 text-[12.5px] text-[#1f6b48]">
            <b className="text-ink">
              {(popVulnerable * (r.coverage_after - coverBaseline) / 100).toLocaleString('fr-FR')}
            </b>{' '}
            personnes vulnérables gagnent un refuge à &lt; 10 min
          </p>
          {/* Détail par action */}
          <div className="mt-2 flex flex-col gap-1">
            {r.by_action.map((a) => (
              <div key={a.id} className="flex justify-between text-[11.5px]">
                <span className="text-[#1f6b48]">{a.label}</span>
                <span className="font-mono font-semibold text-[#1f8a5b]">+{a.cover} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
